import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { heroContent } from '../mockData';
import { Button } from './ui/button';
import { ArrowRight, Check } from 'lucide-react';
import {
  blurFade,
  createFloatingAnimation,
  createStaggerContainer,
  fadeInRight,
  fadeInUp,
  getHeroAnimationProps,
  scaleIn,
} from '../lib/animations';

const HeroSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const heroAnimationProps = getHeroAnimationProps(shouldReduceMotion);
  const containerVariants = shouldReduceMotion
    ? undefined
    : createStaggerContainer({ staggerChildren: 0.25, delayChildren: 0.2 });
  const textStagger = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18 });

  return (
    <section id="accueil" className="relative bg-[#f5ebe5] overflow-hidden">
      {!shouldReduceMotion && (
        <>
          <motion.div
            className="absolute -top-24 -right-16 w-64 h-64 rounded-full bg-[#d4a594]/30 blur-3xl"
            animate={createFloatingAnimation({ offset: 26, duration: 26 })}
          />
          <motion.div
            className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-[#a5400e]/25 blur-3xl"
            animate={createFloatingAnimation({ offset: 22, duration: 28, delay: 4 })}
          />
        </>
      )}
      <motion.div className="max-w-7xl mx-auto" {...heroAnimationProps} variants={containerVariants}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[500px] sm:min-h-[600px]">
          <motion.div className="px-4 sm:px-6 lg:px-12 py-12 sm:py-16 lg:py-20 order-2 lg:order-1" variants={textStagger}>
            <div className="relative">
              <motion.div className="relative z-10" variants={textStagger}>
                <motion.div className="inline-block mb-4 sm:mb-6" variants={scaleIn}>
                  <span className="text-xs sm:text-sm uppercase tracking-widest text-[#a5400e] font-medium bg-[#a5400e]/10 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    {heroContent.badge}
                  </span>
                </motion.div>

                <motion.h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5c2810] mb-4 sm:mb-6 leading-tight" variants={fadeInUp}>
                  {heroContent.title}
                </motion.h1>

                <motion.p className="text-sm sm:text-base text-[#7a4a3a] mb-6 sm:mb-8 leading-relaxed" variants={fadeInUp}>
                  {heroContent.description}
                </motion.p>

                <motion.div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8" variants={textStagger}>
                  {heroContent.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="bg-white/70 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-[#a5400e]/10 shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                      variants={blurFade}
                      whileHover={shouldReduceMotion ? undefined : { translateY: -4, boxShadow: '0 18px 40px rgba(165, 64, 14, 0.18)' }}
                    >
                      <div className="flex items-start space-x-3">
                        <Check className="text-[#a5400e] flex-shrink-0 mt-0.5" size={18} />
                        <div>
                          <h3 className="text-sm sm:text-base font-semibold text-[#5c2810] mb-1">{feature.title}</h3>
                          <p className="text-xs sm:text-sm text-[#7a4a3a]">{feature.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="flex flex-col sm:flex-row gap-3 sm:gap-4" variants={textStagger}>
                  <motion.a
                    href="#contact"
                    className="flex-1 sm:flex-initial"
                    whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  >
                    <Button className="w-full sm:w-auto bg-[#a5400e] hover:bg-[#7a2b0d] text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#a5400e]/20 group">
                      {heroContent.ctaButton1}
                      <ArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-1" size={18} />
                    </Button>
                  </motion.a>
                  <motion.a
                    href="#methode"
                    className="flex-1 sm:flex-initial"
                    whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                  >
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto border-[#a5400e] text-[#a5400e] hover:bg-[#a5400e] hover:text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded-xl transition-all duration-300 hover:shadow-xl"
                    >
                      {heroContent.ctaButton2}
                    </Button>
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div className="relative order-1 lg:order-2 h-[300px] sm:h-[400px] lg:h-[600px] group" variants={fadeInRight}>
            <motion.img
              src={heroContent.image}
              alt="Espace de thérapie calme et serein"
              className="w-full h-full object-cover"
              {...(shouldReduceMotion
                ? {}
                : {
                    initial: { scale: 1.05 },
                    animate: { scale: 1 },
                    transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
                  })}
            />
            {/* Overlay dégradé subtil avec la phrase */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#5c2810]/80 via-[#5c2810]/20 to-transparent" />
            {!shouldReduceMotion && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 p-6 sm:p-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#F5F0E8]/60 to-[#F5F0E8]/60" />
                  <p className="text-[#F5F0E8]/90 text-xs sm:text-sm font-light tracking-wide text-center italic">
                    {"Accompagnement neuro-thérapeutique et de bien être pour rééquilibrer vos systèmes"}
                  </p>
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent via-[#F5F0E8]/60 to-[#F5F0E8]/60" />
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;